import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { useTenant } from '@/tenant/TenantProvider'
import { useTrophyStore } from '@/state/useTrophyStore'
import { useViewStore } from '@/state/useViewStore'
import { isoWeekKey } from '@/lib/workingDay'
import { now } from '@/lib/clock'
import { formatCzkShort, formatNumber, formatDate } from '@/lib/format'
import { Sparkles, Trophy, ArrowUpRight, X } from 'lucide-react'
import { playMotif, unlockAudio } from '@/lib/soundManager'
import { DEFAULT_MILESTONES } from '@/milestones/definitions'
import { describeMilestone } from '@/milestones/labels'
import { StoryProgressBars } from './StoryProgressBars'

const PAGE_DURATION_MS = 11_000
const TOTAL_PAGES = 5

export function WeeklyReportView() {
  const { t } = useTranslation()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const locale = useSettingsStore((s) => s.locale)
  const setViewMode = useViewStore((s) => s.setViewMode)
  const markShown = useViewStore((s) => s.markWeeklyShown)
  const trophies = useTrophyStore((s) => s.unlocked)
  const tenant = useTenant()
  const [page, setPage] = useState(0)

  useEffect(() => {
    void unlockAudio()
    playMotif('weekly-intro')
    return () => undefined
  }, [])

  useEffect(() => {
    const id = window.setTimeout(() => {
      if (page < TOTAL_PAGES - 1) {
        setPage((p) => p + 1)
      } else {
        markShown(isoWeekKey(now()))
        setViewMode('main')
      }
    }, PAGE_DURATION_MS)
    return () => window.clearTimeout(id)
  }, [page, setViewMode, markShown])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        markShown(isoWeekKey(now()))
        setViewMode('main')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setViewMode, markShown])

  if (!snapshot) {
    return (
      <div className="grid h-full w-full place-items-center text-ink-muted">
        <div className="text-center">
          <div className="mb-2 text-sm uppercase tracking-widest text-ink-subtle">
            {t('weekly.title')}
          </div>
          <div className="font-display text-2xl">{t('common.loading')}</div>
        </div>
      </div>
    )
  }

  const recentTrophies = trophies.filter((tr) => {
    const d = new Date(tr.hitAt)
    return Date.now() - d.getTime() < 7 * 86_400_000
  })
  const defs = DEFAULT_MILESTONES.concat(tenant.milestones.customDefinitions ?? [])

  const pages: JSX.Element[] = [
    <Page key={0} kicker={t('weekly.title')} title={t('weekly.greeting')}>
      <p className="max-w-2xl text-2xl leading-relaxed text-ink-muted">
        {locale === 'cs'
          ? 'Pojďme se podívat, co poslední týden přinesl. Drobné věci i ty velké.'
          : "Let's recap the last week — small wins and big moves."}
      </p>
    </Page>,
    <Page
      key={1}
      kicker={t('weekly.lastWeek')}
      title={locale === 'cs' ? 'Týden v číslech' : 'Week in numbers'}
    >
      <div className="grid grid-cols-3 gap-6">
        <Stat
          label={t('kpi.newClients')}
          value={formatNumber(snapshot.clients.newThisMonth, locale)}
        />
        <Stat label={t('kpi.mrr')} value={
            snapshot.revenue.mrrCzk == null
              ? '—'
              : formatCzkShort(snapshot.revenue.mrrCzk, locale)
          } />
        <Stat
          label={t('kpi.closedToday')}
          value={formatNumber(snapshot.incidents.closedToday * 7, locale)}
        />
      </div>
    </Page>,
    <Page key={2} kicker={t('weekly.topPerformers')} title={snapshot.starOfTheWeek?.name ?? '—'}>
      <div className="flex max-w-2xl flex-col gap-3 text-2xl text-ink-muted">
        <span className="text-xl text-brand-accent">{snapshot.starOfTheWeek?.team}</span>
        <p>"{snapshot.starOfTheWeek?.reason}"</p>
      </div>
    </Page>,
    <Page
      key={3}
      kicker={t('weekly.trophies')}
      title={
        recentTrophies.length > 0
          ? locale === 'cs'
            ? `${recentTrophies.length} nových trofejí`
            : `${recentTrophies.length} new trophies`
          : locale === 'cs'
            ? 'Žádné nové trofeje, ale...'
            : 'No new trophies, but...'
      }
    >
      {recentTrophies.length > 0 ? (
        <ul className="grid max-w-3xl grid-cols-2 gap-3">
          {recentTrophies.slice(0, 6).map((tr) => {
            const def = defs.find((d) => d.id === tr.milestoneId)
            const m = def && describeMilestone(def, locale)
            return (
              <li
                key={tr.milestoneId}
                className="flex items-center gap-3 rounded-2xl border border-brand-primary/30 bg-brand-primary/5 px-4 py-3"
              >
                <Trophy className="size-5 text-brand-primary" />
                <span className="text-lg text-ink">{m?.title ?? tr.milestoneId}</span>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="max-w-2xl text-2xl text-ink-muted">
          {locale === 'cs'
            ? 'Cíle byly náročné, ale milníky čekají. Pojďme tento týden lámat rekordy.'
            : 'Goals were tough, but milestones are waiting. Let’s break records this week.'}
        </p>
      )}
    </Page>,
    <Page
      key={4}
      kicker={t('weekly.outlook')}
      title={locale === 'cs' ? 'Co nás čeká' : "What's ahead"}
    >
      <div className="grid grid-cols-2 gap-3 text-lg text-ink-muted">
        <div className="rounded-2xl border border-border bg-surface-2/60 px-4 py-3">
          <div className="text-xs uppercase tracking-widest text-ink-subtle">
            {locale === 'cs' ? 'Pondělní úkol' : 'Monday task'}
          </div>
          <div className="text-xl text-ink">
            {locale === 'cs'
              ? `Vyřešit ${snapshot.incidents.open} otevřených incidentů`
              : `Close ${snapshot.incidents.open} open incidents`}
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-surface-2/60 px-4 py-3">
          <div className="text-xs uppercase tracking-widest text-ink-subtle">{t('kpi.mrr')}</div>
          <div className="text-xl text-ink">
            {locale === 'cs' ? 'cílíme na' : 'targeting'}{' '}
            {formatCzkShort(snapshot.revenue.targetCzk, locale)}
          </div>
        </div>
      </div>
    </Page>,
  ]

  function skip() {
    markShown(isoWeekKey(now()))
    setViewMode('main')
  }

  return (
    <div className="relative grid h-full w-full place-items-center">
      <StoryProgressBars
        totalPages={TOTAL_PAGES}
        currentPage={page}
        durationMs={PAGE_DURATION_MS}
      />
      <button
        type="button"
        onClick={skip}
        title="Esc"
        className="absolute right-6 top-8 z-10 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-1/60 px-3 py-1 text-xs font-medium text-ink-muted backdrop-blur transition hover:border-brand-primary/40 hover:text-ink"
      >
        <X className="size-3" />
        {locale === 'cs' ? 'Přeskočit' : 'Skip'}
      </button>
      <AnimatePresence mode="wait">
        <motion.div
          key={page}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-6xl px-8"
        >
          {pages[page]}
          <div className="mt-12 flex items-center justify-between text-xs text-ink-subtle">
            <span>
              {formatDate(now(), locale)} · {t('weekly.title')}
            </span>
            <span className="hidden items-center gap-2">
              {pages.map((_, i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all duration-300 ${i === page ? 'w-8 bg-brand-primary' : 'w-2 bg-border-strong/40'}`}
                />
              ))}
            </span>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

function Page({
  kicker,
  title,
  children,
}: {
  kicker: string
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-2 rounded-full border border-brand-primary/40 bg-brand-primary/10 px-3 py-1 text-xs uppercase tracking-widest text-brand-primary">
        <Sparkles className="size-3.5" />
        {kicker}
      </div>
      <h1 className="font-display text-7xl font-bold leading-[1.05] tracking-tight text-ink">
        {title}
      </h1>
      {children}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="font-display text-6xl font-bold tabular-nums text-ink">{value}</div>
      <div className="text-xs uppercase tracking-widest text-ink-subtle">
        <ArrowUpRight className="mr-1 inline size-3" />
        {label}
      </div>
    </div>
  )
}
